import type { User } from '@/domain/User';
import { api } from '@/lib/api';
import { queryOptions } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';

export interface UsersFilters {
  [key: string]: string | number | undefined;
  role?: 'Guest' | 'Customer' | 'Doctor' | 'Packer';
  search?: string;
  email?: string;
  per_page?: number;
  doctor_id?: number;
  clinic_id?: number;
}

export interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface UserClinicRelation {
  id: number;
  clinic?: string;
  name?: string;
  pivot?: {
    user_id: number;
    clinic_id: number;
  };
}

export type UserWithClinicData = User & {
  clinic?: { id: number; name: string };
  clinics?: Array<UserClinicRelation>;
};

export interface UsersResponse {
  data: Array<UserWithClinicData>;
  meta: PaginationMeta;
}

export const getUsersQuery = (page: number = 1, filters: UsersFilters) =>
  queryOptions({
    queryKey: queryKeys.users.all(page, filters),
    queryFn: async (): Promise<UsersResponse> => {
      const searchParams = new URLSearchParams();
      searchParams.set('page', String(page));
      if (filters.role) {
        searchParams.set('role', filters.role);
      }
      if (filters.search) {
        searchParams.set('search', filters.search);
      }
      if (filters.email) {
        searchParams.set('email', filters.email);
      }
      if (filters.per_page) {
        searchParams.set('per_page', String(filters.per_page));
      }
      if (filters.doctor_id) {
        searchParams.set('doctor_id', String(filters.doctor_id));
      }
      if (filters.clinic_id) {
        searchParams.set('clinic_id', String(filters.clinic_id));
      }
      return await api
        .get(`users?${searchParams.toString()}`)
        .json<UsersResponse>();
    },
  });

export const getAllUsersQuery = (filters: UsersFilters) =>
  queryOptions({
    queryKey: [...queryKeys.users.all(undefined, filters), 'all'],
    queryFn: async (): Promise<Array<UserWithClinicData>> => {
      const searchParams = new URLSearchParams();
      searchParams.set('per_page', '100');
      if (filters.role) {
        searchParams.set('role', filters.role);
      }
      if (filters.search) {
        searchParams.set('search', filters.search);
      }
      if (filters.email) {
        searchParams.set('email', filters.email);
      }

      const firstResponse = await api
        .get(`users?${searchParams.toString()}`)
        .json<UsersResponse>();

      let allData = [...firstResponse.data];
      const lastPage = firstResponse.meta.last_page;

      if (lastPage > 1) {
        const promises = [];
        for (let page = 2; page <= lastPage; page++) {
          const params = new URLSearchParams(searchParams);
          params.set('page', String(page));
          promises.push(
            api.get(`users?${params.toString()}`).json<UsersResponse>(),
          );
        }

        const responses = await Promise.all(promises);
        responses.forEach((res) => {
          allData = [...allData, ...res.data];
        });
      }

      return allData;
    },
  });
